import { ArrowUpRight, Camera, Clock3, MapPin, Package, UserRound, Wrench, X } from "lucide-react";

const QUICK_MINUTE_OPTIONS = [30, 45, 60, 90, 120, 180, 240];

export default function WerkbonDrawer({
  item,
  planningDraft,
  monteurs,
  scopes,
  planningSaving,
  plannerMessage,
  onClose,
  onPlanningChange,
  onPlanningSave,
  onSendBackToDispatch,
}) {
  return (
    <aside className="flex h-full min-h-[640px] flex-col rounded-[26px] border border-slate-200 bg-white shadow-sm">
      <div className="flex items-start justify-between border-b border-slate-100 px-4 py-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Werkbon detail</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-950">
            {item ? item.werkbonNummer || "Werkbon" : "Geen werkbon geselecteerd"}
          </h2>
        </div>
        {item ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      {!item ? (
        <div className="flex flex-1 items-center justify-center px-5 text-center text-sm text-slate-500">
          Selecteer een werkbon vanuit de werkvoorraad, dagplanning of projecttijdlijn om details te openen.
        </div>
      ) : (
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <DetailBlock title="Nu beslissen (planning)">
            <p className="text-xs text-slate-500">
              Kies minimaal monteur, datum en tijd om deze werkbon echt in de dagplanning te zetten.
            </p>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Scope / Regio</span>
              <select
                value={planningDraft.scopeId}
                onChange={(e) => onPlanningChange("scopeId", e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
              >
                <option value="">Geen scope</option>
                {(Array.isArray(scopes) ? scopes : []).map((scope) => (
                  <option key={scope._id} value={scope._id}>
                    {[scope.type, scope.name].filter(Boolean).join(" • ")}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Monteur</span>
              <select
                value={planningDraft.assignedTo}
                onChange={(e) => onPlanningChange("assignedTo", e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
              >
                <option value="">Niet toegewezen</option>
                {monteurs.map((monteur) => (
                  <option key={monteur._id} value={monteur._id}>
                    {monteur.naam || monteur.email || monteur._id}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Datum</span>
                <input
                  type="date"
                  value={planningDraft.plannedDate}
                  onChange={(e) => onPlanningChange("plannedDate", e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">Tijd</span>
                <input
                  type="time"
                  value={planningDraft.plannedStartTime}
                  onChange={(e) => onPlanningChange("plannedStartTime", e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">Geplande tijd (blokken)</span>
              <div className="mb-2 flex flex-wrap gap-2">
                {QUICK_MINUTE_OPTIONS.map((minutes) => (
                  Number(planningDraft.plannedMinutes) === minutes
                  ? (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => onPlanningChange("plannedMinutes", String(minutes))}
                    className="rounded-full border border-slate-900 bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white"
                  >
                    {minutes} min
                  </button>
                  ) : (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => onPlanningChange("plannedMinutes", String(minutes))}
                    className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-slate-300"
                  >
                    {minutes} min
                  </button>
                  )
                ))}
              </div>
              <input
                type="number"
                min="1"
                step="1"
                value={planningDraft.plannedMinutes}
                onChange={(e) => onPlanningChange("plannedMinutes", e.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-slate-400 focus:bg-white"
                placeholder="Aantal minuten"
              />
            </label>
          </DetailBlock>

          <DetailBlock title="Context">
            <InfoLine icon={UserRound} label="Klant" value={item.clientName} />
            <InfoLine icon={MapPin} label="Locatie" value={item.locationLabel} />
            <InfoLine icon={Wrench} label="Project" value={item.projectLabel} />
            <InfoLine icon={Clock3} label="Beschrijving" value={item.title} />
          </DetailBlock>

          <DetailBlock title="Uitvoering (huidige stand)">
            <InfoLine icon={Clock3} label="Uren" value={String(item.hourCount)} />
            <InfoLine icon={Package} label="Materialen" value={String(item.materialCount)} />
            <InfoLine icon={Camera} label="Foto's" value={String(item.photoCount)} />
          </DetailBlock>

          {plannerMessage ? (
            <div className={`rounded-2xl px-3 py-3 text-sm ${plannerMessage.tone}`}>
              {plannerMessage.text}
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onPlanningSave}
              disabled={planningSaving}
              className="rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60"
            >
              {planningSaving ? "Opslaan..." : "Opslaan en inplannen"}
            </button>
            <button
              type="button"
              onClick={onSendBackToDispatch}
              disabled={planningSaving}
              className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
            >
              Terug naar werkvoorraad
            </button>
            <a
              href={`/werkbon/${item.id}`}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Open volledige werkbon pagina
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      )}
    </aside>
  );
}

function DetailBlock({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm text-slate-700">
      <Icon size={15} className="mt-0.5 text-slate-400" />
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-500">{label}</p>
        <p className="mt-0.5">{value || "-"}</p>
      </div>
    </div>
  );
}
